import { useApi } from '@data-access';
import { CookieHelper } from '@helpers';
import { viewSettingsStore } from '@stores';
import { notionStore } from '@stores/notion.store';
import { AppRoutes } from '@types';
import { useRouter } from 'next/router';
import { createContext, useCallback, useMemo } from 'react';

type AuthorizationContextType = {
  login: (username: string, password: string, setError: any) => void;
  logout: () => void;
};

export const AuthorizationContext = createContext<AuthorizationContextType>({
  login: (username: string, password: string) => {},
  logout: () => null,
});

type AuthorizationProviderProps = {
  children: React.ReactNode;
};
export const ACCESS_TOKEN = 'access_token';
export const REFRESH_TOKEN = 'refresh_token';

export const AuthorizationProvider: React.FC<AuthorizationProviderProps> = ({ children }) => {
  const { auth, api, accounts, setToken } = useApi();
  const { push } = useRouter();
  const { setValues } = notionStore;

  const { setViewSetting } = viewSettingsStore;

  const login = useCallback(
    async (username: string, password: string, setError?: any) => {
      try {
        // get access token
        const { access, refresh } = await auth.apiJwtTokenCreate({
          tokenObtainPair: { username, password },
        });

        CookieHelper.set(ACCESS_TOKEN, access, undefined);
        CookieHelper.set(REFRESH_TOKEN, refresh, undefined);

        // set token in api provider config
        setToken(access);
        setValues();
        setViewSetting();

        push(AppRoutes.usecasesInventory);
      } catch (error) {
        if ((error as any).response?.status === 401) {
          setError('password', { message: 'No active account found with the given credentials' });
        }
      }
    },
    [auth, push, setToken, setValues, setViewSetting],
  );

  const logout = useCallback(async () => {
    CookieHelper.remove(ACCESS_TOKEN);
    CookieHelper.remove(REFRESH_TOKEN);

    // Clear the Django session so session-protected views (e.g. /docs/) deny
    // access immediately after logout. Await so the session is gone before
    // the browser navigates (prevents a race where /docs/ is hit while the
    // old session is still live).
    try {
      await accounts.accountsLogoutCreate();
    } catch {
      // ignore — session expires in ≤30 min regardless
    }

    push(AppRoutes.login);
  }, [accounts, push]);

  const value = useMemo(
    () => ({
      login,
      logout,
    }),
    [login, logout],
  );

  return <AuthorizationContext.Provider value={value}>{children}</AuthorizationContext.Provider>;
};
